import * as React from 'react';
import * as styles from './AdvancedSettings.css';
import AdvancedSettingsService from '../../services/AdvancedSettingsService';
import { LocalizationService } from '../../services/LocalizationService';
import { GroupListGroup, GroupList, GroupListColumnType } from '../../../src/interfaces/IGroupList'
import { IAGroupList } from '../../../src/components/groupList/GroupList';

export interface IAdvancedSettingsProps {
  environment: "Development" | "Test" | "Production";
  token: string;
  tenant: string;
  minWidth?: number;
  updated: () => void;
}

export interface IAdvancedSettingsState {
  items;
}

export class IAAdvancedSettings extends React.Component<IAdvancedSettingsProps, IAdvancedSettingsState> {

  private localizationService: LocalizationService;
  private columnsTitle;

  constructor(props: IAdvancedSettingsProps) {
    super(props);

    this.localizationService = new LocalizationService();
    this.localizationService.checkLocalizedStrings().then(() => this.forceUpdate());

    this.state = {
      items: { id: "", list: [] }
    }

    this.columnsTitle = [{ title: "", name: "toggle" }];

  }

  private showInAdmin(key: string): boolean {
    switch (key) {
      case "showNewMobileApp":
      case "useBlobImages":
      case "enableGraphPersonCard":
      case "enableMessageAutoRefresh":
      case "enableADTargetingComponent":
      case "version":
      case "shouldUpdateLocalCache":
      case "enableCodeSnippetComponent":
      case "trackingId":
      case "trackingType":
      case "showToolbox":
        return true;
      default:
        return false;
    }
  }

  public componentDidMount(): void {
    AdvancedSettingsService.get(this.props.environment, this.props.token, this.props.tenant).then(res => {
      console.log(res);
      var items = {};
      for (const group in res) {
        if (group !== "hidden" && group !== "cache" && group !== "cacheExpiration" && group !== "cacheTTL") {
          items[group] = [];
          for (const key in res[group]) {
            if (this.showInAdmin(key)) {
              var id = res[group][key].key;
              var value = res[group][key].value;
              // determines if the setting is a true/false setting or a free text setting
              // the API always returns a string, so we check if it is equals to "true" or "false"
              var isSwitch: boolean;
              var type: GroupListColumnType;
              if (value === "true" || value === "false") {
                isSwitch = true;
                type = GroupListColumnType.Switch;
              } else {
                isSwitch = false;
                type = GroupListColumnType.TextField;
              }
              items[group].push({
                title: this.localizationService.getFromString("AdvancedSettings_" + group + "_" + id + "_title"),
                id: id,
                name: id,
                columns: [
                  {
                    field: "toggle",
                    title: "Toggle",
                    type: type,
                    value: isSwitch ? value == "true" : value,
                    disabled: false,
                    toolTip: "AdvancedSettings_" + group + "_" + id + "_description" === this.localizationService.getFromString("AdvancedSettings_" + group + "_" + id + "_description") ? "" : this.localizationService.getFromString("AdvancedSettings_" + group + "_" + id + "_description")
                  }
                ]
              });
            }
          }
        }
      }
      this.setState({
        items: items
      });
    });
  }

  public settingChanged(settings, item, group): void {

    var lowerCaseGroup = group.charAt(0).toLowerCase() + group.slice(1);
    var updatedItemValue = settings[group][item.id].Toggle;

    var newState = this.state.items;

    // finds the changed item in the state
    for (let i = 0; i < newState[lowerCaseGroup].length; i++) {
      var stateItem = newState[lowerCaseGroup][i];

      if (stateItem.id === item.id) {

        var oldValue = stateItem.columns[0].value;

        // checks if item has actually changed
        if (stateItem.columns[0].value !== updatedItemValue) {
          newState[lowerCaseGroup][i].columns[0].disabled = true;
          newState[lowerCaseGroup][i].columns[0].value = updatedItemValue;
          this.setState({
            items: newState
          });

          AdvancedSettingsService.update(this.props.environment, this.props.token, this.props.tenant, lowerCaseGroup, item.id, updatedItemValue.toString())
            .then(() => {
              newState[lowerCaseGroup][i].columns[0].value = updatedItemValue;
              newState[lowerCaseGroup][i].columns[0].disabled = false;
              this.setState({
                items: newState
              });
              this.props.updated();
            })
            .catch(() => {
              console.log("Failed changing setting:", item.id);
              newState[lowerCaseGroup][i].columns[0].disabled = false;
              newState[lowerCaseGroup][i].columns[0].value = oldValue;
              this.setState({
                items: newState
              });
            });
        }
      }
    }
  }

  public render(): JSX.Element {
    var group: GroupListGroup[];
    var groups: GroupList;

    group = [{
      name: "admin",
      expanded: true,
      title: "Admin",
      items: this.state.items["admin"] ?? []
    }, {
      name: "image",
      expanded: false,
      title: "Image",
      items: this.state.items["image"] ?? []
    }, {
      name: "app",
      expanded: false,
      title: "App",
      items: this.state.items["app"] ?? []
    }, {
      name: "home",
      expanded: false,
      title: "Home",
      items: this.state.items["home"] ?? []
    }, {
      name: "messages",
      expanded: false,
      title: "Messages",
      items: this.state.items["messages"] ?? []
    }, {
      name: "divider",
      expanded: false,
      title: "Divider",
      items: this.state.items["divider"] ?? []
    }, {
      name: "authentication",
      expanded: false,
      title: "Authentication",
      items: this.state.items["authentication"] ?? []
    }, {
      name: "cache",
      expanded: false,
      title: "Cache",
      items: this.state.items["cache"] ?? []
    }, {
      name: "cacheExpiration",
      expanded: false,
      title: "Cache Expiration",
      items: this.state.items["cacheExpiration"] ?? []
    }, {
      name: "cacheTLL",
      expanded: false,
      title: "Cache TLL",
      items: this.state.items["cacheTLL"] ?? []
    }, {
      name: "event",
      expanded: false,
      title: "Event",
      items: this.state.items["event"] ?? []
    }, {
      name: "navigator",
      expanded: false,
      title: "Navigator",
      items: this.state.items["navigator"] ?? []
    }, {
      name: "tracking",
      expanded: false,
      title: "Tracking",
      items: this.state.items["tracking"] ?? []
    }];

    var groupCleaned = [];

    group.forEach(e => {
      if (e.items.length !== 0) {
        groupCleaned.push(e);
      }
    });

    groups = {
      columns: this.columnsTitle,
      groups: groupCleaned,
      nameFirstColumn: "",
    }

    return (
      <div>
        <div className={styles.description}>
          {this.localizationService.strings.AdvancedSettings_Description?.split('\n').map((item, i) => <p key={i}>{item}</p>)}
        </div>
        <IAGroupList
          highlightColor={"#025159"}
          style={{ minWidth: this.props.minWidth }}
          groupsItem={groups}
          getUpdates={(items, item, group) => { this.settingChanged(items, item, group) }}
          hideHeader={true}
        />
      </div>
    );
  }
}
